import React, { useCallback, useId, useRef, useState } from 'react';
import clsx from 'clsx';

export const DESCRIPTION_LINE_CLAMP = 3;
const CLAMPED_CLASS = 'cp_price-picker-option-description--clamped';

// Rounding can leave scrollHeight a fraction above clientHeight on text that
// fits, which would put a Show more link under a description with nothing to show.
const OVERFLOW_TOLERANCE_PX = 1;

/**
 * Whether the clamped text is hiding anything, measured on the element itself so
 * a description that fits in the clamp gets no toggle. Re-measured whenever the
 * element resizes (a narrower column wraps more lines). Measuring is skipped
 * while the text is expanded - unclamped, nothing overflows - so the answer from
 * the last clamped layout stands until it collapses again.
 */
function useClampOverflow() {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const observerRef = useRef(null);

  const ref = useCallback(node => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    const measure = () => {
      if (!node.classList.contains(CLAMPED_CLASS)) return;
      setIsOverflowing(node.scrollHeight > node.clientHeight + OVERFLOW_TOLERANCE_PX);
    };

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      observerRef.current = new ResizeObserver(measure);
      observerRef.current.observe(node);
    }
  }, []);

  return [isOverflowing, ref];
}

/**
 * A price option's description, cut to its first lines behind a Show more toggle
 * that only appears when there is more to show. Line breaks the seller typed are
 * kept. With `isShortenable` off (the theme's Show more / less switch) the whole
 * text is shown.
 */
export default function PriceDescription({ text, isShortenable = true, labels = {} }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, measureRef] = useClampOverflow();
  const id = useId();

  const isClamped = isShortenable && !isExpanded;

  return (
    <>
      <div
        ref={measureRef}
        id={id}
        className={clsx('cp_price-picker-option-description-text whitespace-pre-line', {
          [CLAMPED_CLASS]: isClamped,
        })}
        style={
          isClamped
            ? {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: DESCRIPTION_LINE_CLAMP,
                overflow: 'hidden',
              }
            : undefined
        }
      >
        {text}
      </div>
      {isShortenable && isOverflowing && (
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-controls={id}
          // The option card selects on any click inside it, so the toggle has to
          // keep its click to itself.
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            setIsExpanded(current => !current);
          }}
          className="cp_price-picker-option-description-toggle cursor-pointer font-semibold underline"
        >
          {isExpanded ? (labels.showLessText ?? 'Show less') : (labels.showMoreText ?? 'Show more')}
        </button>
      )}
    </>
  );
}
